/**
 * API client.
 *
 * The session lives in an HttpOnly cookie, so every request sends credentials and
 * mutating requests carry the CSRF token the login response handed us. A 401
 * anywhere means the session is gone: we clear local state and let the router
 * bounce to /login rather than leaving half-authenticated screens on display.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly body: unknown = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const CSRF_KEY = "mdc_csrf";

export function setCsrfToken(token: string | null) {
  if (token) sessionStorage.setItem(CSRF_KEY, token);
  else sessionStorage.removeItem(CSRF_KEY);
}

export function getCsrfToken(): string | null {
  return sessionStorage.getItem(CSRF_KEY);
}

// ── the partner a member of MultiDoc staff is working inside ────────────────
//
// Set only by "Work inside" when partner consent is enforced: staff then see a
// partner's pursuits, files, knowledge base, CRM and demo sign-in only from
// inside it (``X-Org-Id``) while it has granted access. The CRM's own
// "Whose CRM" picker keeps its own header. The server ignores the header for
// anyone who is not MultiDoc staff.

const WORKING_KEY = "mdc.working.org";
let workingOrg: string | null = (() => {
  try { return sessionStorage.getItem(WORKING_KEY); } catch { return null; }
})();

export function getWorkingOrg(): string | null {
  return workingOrg;
}

export function setWorkingOrg(org: string | null): void {
  if (org === workingOrg) return;
  workingOrg = org;
  try {
    if (org) sessionStorage.setItem(WORKING_KEY, org);
    else sessionStorage.removeItem(WORKING_KEY);
  } catch { /* private window */ }
  window.dispatchEvent(new CustomEvent("mdc:working-org", { detail: org }));
}

/** Headers for a raw ``fetch`` that bypasses ``api()``. */
export function workingHeaders(): Record<string, string> {
  return workingOrg ? { "X-Org-Id": workingOrg } : {};
}

/** A GET URL for an ``<img>`` or a link, which cannot carry a header. */
export function orgUrl(url: string): string {
  if (!workingOrg) return url;
  return `${url}${url.includes("?") ? "&" : "?"}_org=${encodeURIComponent(workingOrg)}`;
}

type Options = {
  method?: string; body?: unknown; signal?: AbortSignal;
  /** Extra request headers, e.g. the organisation a vendor is acting in. */
  headers?: Record<string, string>;
};

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = { ...workingHeaders(), ...opts.headers };
  // A FormData body sets its own multipart boundary, so it gets no Content-Type.
  const form = opts.body instanceof FormData;
  if (opts.body !== undefined && !form) headers["Content-Type"] = "application/json";
  if (method !== "GET" && method !== "HEAD") {
    const csrf = getCsrfToken();
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }

  const resp = await fetch(path.startsWith("/api/") ? path : `/api${path}`, {
    method,
    headers,
    credentials: "include",
    signal: opts.signal,
    body: opts.body === undefined ? undefined : form ? (opts.body as FormData) : JSON.stringify(opts.body),
  });

  if (resp.status === 204) return undefined as T;

  let payload: unknown = null;
  const text = await resp.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!resp.ok) {
    const body = payload as { error?: string; detail?: string } | null;
    if (resp.status === 401) {
      setCsrfToken(null);
      window.dispatchEvent(new Event("mdc:unauthorized"));
    }
    throw new ApiError(
      resp.status,
      body?.error ?? `http_${resp.status}`,
      body?.detail ?? `Request failed (${resp.status})`,
      payload,
    );
  }
  return payload as T;
}

export const get = <T>(path: string) => api<T>(path);
export const post = <T>(path: string, body?: unknown) => api<T>(path, { method: "POST", body });
export const patch = <T>(path: string, body: unknown) => api<T>(path, { method: "PATCH", body });
export const del = <T>(path: string) => api<T>(path, { method: "DELETE" });
/** Multipart POST, with the same CSRF header and error handling as the rest. */
export const upload = <T>(path: string, form: FormData) => api<T>(path, { method: "POST", body: form });
export const put = <T>(path: string, body: unknown) => api<T>(path, { method: "PUT", body });
